import type { ListOverrideAudience } from '@supply-mover-2-0/shared';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { Select } from '../../../components/forms/Select';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';

/** Where an edit lands: which lists it covers and who it is for (PD-14278). */
export interface OverrideSlot {
	scope: 'list' | 'entity';
	audience: ListOverrideAudience;
	roleId: number | null;
}

export const EVERYONE_SLOT: OverrideSlot = {
	scope: 'list',
	audience: 'organization',
	roleId: null,
};

export function slotKey(slot: OverrideSlot): string {
	return `${slot.scope}:${slot.audience}:${String(slot.roleId ?? '')}`;
}

export function slotLabel(
	slot: OverrideSlot,
	roles: Array<{ id: number; name: string }>,
	entityLabel: string,
	t: (text: string) => string,
): string {
	const scope =
		slot.scope === 'entity' ? t(`All ${entityLabel} lists`) : t('This list');
	const who =
		slot.audience === 'role'
			? (roles.find((role) => role.id === slot.roleId)?.name ?? t('Role'))
			: t('Everyone');
	return `${scope} · ${who}`;
}

interface OverrideSlotPickerProps {
	slot: OverrideSlot;
	roles: Array<{ id: number; name: string }>;
	entityLabel: string;
	/** Slots that already hold an override, for the "customized" marks. */
	existing: ReadonlySet<string>;
	disabled?: boolean;
	onChange: (slot: OverrideSlot) => void;
}

/**
 * The "editing for" control on a standard list: scope × audience. A change
 * here reloads the editor with the list as it stands beneath and at that
 * slot, so what is on screen is always what that audience actually gets.
 */
export function OverrideSlotPicker({
	slot,
	roles,
	entityLabel,
	existing,
	disabled = false,
	onChange,
}: OverrideSlotPickerProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const mark = (candidate: OverrideSlot): string =>
		existing.has(slotKey(candidate)) ? ' •' : '';

	const scopeOptions = [
		{
			value: 'list',
			label: `${t('This list')}${mark({ ...slot, scope: 'list' })}`,
		},
		{
			value: 'entity',
			label: `${t(`All ${entityLabel} lists`)}${mark({ ...slot, scope: 'entity' })}`,
		},
	];

	const audienceOptions = [
		{
			value: 'organization',
			label: `${t('Everyone')}${mark({ ...slot, audience: 'organization', roleId: null })}`,
		},
		...roles.map((role) => ({
			value: `role:${String(role.id)}`,
			label: `${t(role.name)}${mark({ ...slot, audience: 'role', roleId: role.id })}`,
		})),
	];

	const audienceValue =
		slot.audience === 'role'
			? `role:${String(slot.roleId ?? '')}`
			: 'organization';

	return (
		<div className="flex items-center gap-2 flex-wrap">
			<span
				className={clsx('text-xs font-medium', colors.classes.textSecondary)}
			>
				{t('Editing for')}
			</span>
			<div className="w-48">
				<Select
					size="xs"
					options={scopeOptions}
					value={slot.scope}
					disabled={disabled}
					onChange={(value) => {
						onChange({
							...slot,
							scope: value === 'entity' ? 'entity' : 'list',
						});
					}}
				/>
			</div>
			<div className="w-48">
				<Select
					size="xs"
					options={audienceOptions}
					value={audienceValue}
					disabled={disabled}
					onChange={(value) => {
						if (value.startsWith('role:')) {
							const roleId = Number.parseInt(value.slice(5), 10);
							onChange({
								...slot,
								audience: 'role',
								roleId: Number.isNaN(roleId) ? null : roleId,
							});
						} else {
							onChange({ ...slot, audience: 'organization', roleId: null });
						}
					}}
				/>
			</div>
			<span className={clsx('text-xs', colors.classes.textMuted)}>
				{t('• = already customized')}
			</span>
		</div>
	);
}
